import { useEffect, useRef } from 'react';
import type { StreamingMessageState } from '../types/contentBlock';
import type { ChatMessage } from '../types/aiChat';
import { nowIso } from '../types/aiChat';

export interface UseStreamingSyncParams {
  /** useStreamingMessage 返回的 state */
  readonly streamingState: StreamingMessageState;
  /** 消息数组 setter */
  readonly setMessages: React.Dispatch<React.SetStateAction<readonly ChatMessage[]>>;
  /** 滚动到底部 */
  readonly scrollToBottom: (force?: boolean) => void;
  /** 外部传入的 ref，标记当前流式消息 ID */
  readonly streamingMsgIdRef: React.MutableRefObject<string | null>;
  /** 流式完成后回调 */
  readonly onCompleted?: (state: StreamingMessageState) => void;
  /** 流式出错后回调 */
  readonly onError?: (state: StreamingMessageState) => void;
}

/**
 * 将 useStreamingMessage 的状态同步到 messages 数组中的对应消息
 */
export const useStreamingSync = ({
  streamingState,
  setMessages,
  scrollToBottom,
  streamingMsgIdRef,
  onCompleted,
  onError
}: UseStreamingSyncParams): void => {
  // ★ 用 ref 包装回调，避免引用变化触发 effect 重执行
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // ★ 流式状态 → messages 数组同步
  useEffect(() => {
    const msgId = streamingMsgIdRef.current;
    if (!msgId) return;
    if (streamingState.status === 'idle') return;

    setMessages(prev =>
      prev.map(m => {
        if (m.id !== msgId) return m;

        const isCompleted = streamingState.status === 'completed';
        const isError = streamingState.status === 'error';

        return {
          ...m,
          content: streamingState.content,
          thinkingItems:
            streamingState.thinkingItems.length > 0
              ? streamingState.thinkingItems
              : m.thinkingItems,
          files:
            streamingState.files.length > 0
              ? streamingState.files
              : m.files,
          workflowTrace: streamingState.workflowTrace ?? m.workflowTrace,
          status: isCompleted ? 'done' : isError ? 'error' : 'thinking',
          ...(isCompleted || isError ? { dateCreated: nowIso() } : {})
        };
      })
    );

    if (streamingState.status === 'streaming') {
      scrollToBottom();
    }
    if (streamingState.status === 'completed' || streamingState.status === 'error') {
      scrollToBottom(true);
    }
  }, [streamingState, setMessages, scrollToBottom, streamingMsgIdRef]);

  // ★ 完成/错误后回调 — 通过 ref 调用，不依赖回调引用
  // ★ 加 lastHandledStatusRef 防止同一个 completed 被多次处理
  const lastHandledStatusRef = useRef<string>('idle');

  useEffect(() => {
    // 只在 status 真正变化时处理，防止重复触发
    if (streamingState.status === lastHandledStatusRef.current) return;
    lastHandledStatusRef.current = streamingState.status;

    if (streamingState.status === 'completed') {
      onCompletedRef.current?.(streamingState);
      streamingMsgIdRef.current = null;
    }
    if (streamingState.status === 'error') {
      onErrorRef.current?.(streamingState);
      streamingMsgIdRef.current = null;
    }
  }, [streamingState, streamingMsgIdRef]); // ★ 不依赖 onCompleted/onError
};